import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, apiError } from "@/lib/api";
import { CalendarBlank, CheckCircle, Trophy, HeartStraight, Warning, Barbell, ForkKnife, ChatCircleText, PaperPlaneTilt, Circle, X, CaretRight, User, Sparkle, Lightbulb, Pulse, Smiley, SmileyMeh, SmileySad } from "@phosphor-icons/react";

/**
 * CoachSundayCheckins — one-glance inbox of every client's Sunday check-in
 * for the current ISO week. Coach reviews each, replies (mirrored to DM +
 * push), and marks reviewed. Powers the coach's Sunday-evening ritual.
 */
const STATUS_STYLE = {
  pending: { border: "border-[#FF3333]", bg: "bg-[#FF3333]/5", text: "text-[#FF3333]", label: "Waiting for you" },
  reviewed: { border: "border-[#00CC66]/40", bg: "bg-[#00CC66]/5", text: "text-[#00CC66]", label: "Reviewed" },
  missing: { border: "border-[#262626]", bg: "", text: "text-zinc-500", label: "Not submitted" },
};

export default function CoachSundayCheckins() {
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [replies, setReplies] = useState({}); // checkin_id -> draft text
  const [sending, setSending] = useState({});
  const [aiState, setAiState] = useState({}); // checkin_id -> {loading, suggestions, err}
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setErr("");
    try {
      const { data } = await http.get("/coach/sunday-checkins");
      setData(data);
      // Pre-fill draft replies with any already-persisted coach_reply
      const preDrafts = {};
      for (const row of data.rows) {
        if (row.checkin?.coach_reply) preDrafts[row.checkin.id] = row.checkin.coach_reply;
      }
      setReplies((r) => ({ ...preDrafts, ...r }));
    } catch (e) { setErr(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const sendReply = async (row) => {
    if (!row.checkin || !replies[row.checkin.id]?.trim()) return;
    setSending((s) => ({ ...s, [row.checkin.id]: true }));
    try {
      await http.post(`/coach/sunday-checkins/${row.checkin.id}/reply`, {
        coach_reply: replies[row.checkin.id].trim(),
      });
      await load();
    } catch (e) { setErr(apiError(e, "Send failed.")); }
    finally { setSending((s) => ({ ...s, [row.checkin.id]: false })); }
  };

  const markReviewed = async (row) => {
    if (!row.checkin) return;
    setSending((s) => ({ ...s, [row.checkin.id]: true }));
    try {
      await http.post(`/coach/sunday-checkins/${row.checkin.id}/mark-reviewed`);
      await load();
    } catch (e) { setErr(apiError(e)); }
    finally { setSending((s) => ({ ...s, [row.checkin.id]: false })); }
  };

  const requestAiSuggestions = async (checkinId) => {
    setAiState((s) => ({ ...s, [checkinId]: { ...(s[checkinId] || {}), loading: true, err: "" } }));
    try {
      const { data } = await http.post(`/coach/sunday-checkins/${checkinId}/ai-suggest`);
      setAiState((s) => ({ ...s, [checkinId]: { loading: false, suggestions: data.suggestions, cached: data.cached } }));
    } catch (e) {
      setAiState((s) => ({ ...s, [checkinId]: { loading: false, err: apiError(e, "AI call failed.") } }));
    }
  };

  const appendCoachingNote = (checkinId, note) => {
    // Append the AI's coaching-note to whatever's already in the reply draft
    setReplies((r) => ({ ...r, [checkinId]: r[checkinId] ? r[checkinId] + "\n\n" + note : note }));
  };

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto pb-24" data-testid="coach-sunday-checkins-page">
      <div className="id-label mb-3 flex items-center gap-2"><CalendarBlank size={12} weight="fill" /> — Sunday inbox —</div>
      <h1 className="id-h1 mb-2">This Week&apos;s Check-ins</h1>
      <p className="text-zinc-400 font-manrope text-sm max-w-2xl mb-6">
        Every client&apos;s answers for this week. Reply here — client gets a DM + push. Mark reviewed when you&apos;ve adjusted their plan.
      </p>

      {err && <div className="border border-[#FF3333] bg-[#FF3333]/10 px-4 py-3 text-sm text-[#FF3333] mb-4">{err}</div>}
      {loading && <div className="text-xs text-zinc-500">Loading…</div>}

      {data && (
        <>
          {/* Progress tiles */}
          <div className="grid grid-cols-3 gap-2 mb-6" data-testid="sunday-inbox-stats">
            <StatTile label="Pending" value={data.pending} color="text-[#FF3333]" border="border-[#FF3333]/50 bg-[#FF3333]/5" />
            <StatTile label="Reviewed" value={data.reviewed} color="text-[#00CC66]" border="border-[#00CC66]/40 bg-[#00CC66]/5" />
            <StatTile label="Not submitted" value={data.missing} color="text-zinc-500" border="border-[#262626]" />
          </div>

          {data.rows.length === 0 && (
            <div className="id-card text-center py-10">
              <div className="text-sm text-zinc-500 font-manrope">No clients on the roster yet.</div>
            </div>
          )}

          <div className="space-y-3">
            {data.rows.map((row) => {
              const s = STATUS_STYLE[row.status];
              const isOpen = openId === (row.checkin?.id || row.client_id);
              const c = row.checkin;
              return (
                <div key={row.client_id} className={"border " + s.border + " " + s.bg} data-testid={`sunday-row-${row.client_id}`}>
                  <button
                    onClick={() => setOpenId(isOpen ? null : (c?.id || row.client_id))}
                    className="w-full flex items-center justify-between gap-3 p-4 text-left"
                    data-testid={`sunday-row-toggle-${row.client_id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 bg-[#1A1A1A] border border-[#262626] flex items-center justify-center shrink-0">
                        <User size={16} weight="bold" className="text-zinc-400" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-oswald text-lg uppercase tracking-wide text-white truncate">{row.client_name}</div>
                        <div className={"text-[10px] font-manrope uppercase tracking-[0.15em] " + s.text}>
                          {s.label}
                          {c?.on_track_score != null && <span className="text-zinc-500"> · Score {c.on_track_score}/5</span>}
                          {c?.wellness && (
                            <span className="text-zinc-500">
                              {" · "}Energy {c.wellness.energy}/10 · Stress {c.wellness.stress_levels}/10
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {row.status !== "missing" && (
                      <CaretRight size={14} weight="bold" className={"transition-transform text-zinc-500 " + (isOpen ? "rotate-90" : "")} />
                    )}
                  </button>

                  {isOpen && c && (
                    <div className="border-t border-[#262626] px-4 pb-4 pt-3 space-y-3" data-testid={`sunday-row-details-${row.client_id}`}>
                      {c.wellness && <WellnessBlock w={c.wellness} clientId={row.client_id} />}
                      <AnswerBlock icon={Trophy} label="Health + fitness wins" text={c.health_wins} />
                      <AnswerBlock icon={HeartStraight} label="Life wins" text={c.life_wins} />
                      <AnswerBlock icon={Warning} label="Distractions" text={c.distractions} />
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <AnswerBlock icon={Barbell} label="Exercises loved" text={c.exercise_likes} compact />
                        <AnswerBlock icon={Barbell} label="Exercises disliked" text={c.exercise_dislikes} compact />
                        <AnswerBlock icon={ForkKnife} label="Meals loved" text={c.meal_likes} compact />
                        <AnswerBlock icon={ForkKnife} label="Meals disliked" text={c.meal_dislikes} compact />
                      </div>
                      <AnswerBlock icon={ChatCircleText} label="Anything else" text={c.anything_else} />

                      {/* AI plan-adjustment suggestions — Claude reads the
                          answers + current plans and returns concrete swaps.
                          Coach hits "Get suggestions" to fire; result cached
                          on the check-in doc so re-clicks don't burn tokens. */}
                      <div className="border-t border-[#262626] pt-3 mt-3" data-testid={`ai-suggest-panel-${c.id}`}>
                        {!aiState[c.id]?.suggestions && !aiState[c.id]?.loading && (
                          <button
                            onClick={() => requestAiSuggestions(c.id)}
                            className="inline-flex items-center gap-1.5 text-[10px] font-oswald uppercase tracking-[0.15em] font-bold border border-[#FF3333]/50 text-[#FF3333] hover:bg-[#FF3333]/10 px-3 py-1.5"
                            data-testid={`ai-suggest-btn-${c.id}`}
                          >
                            <Sparkle size={11} weight="fill" /> Get AI suggestions
                          </button>
                        )}
                        {aiState[c.id]?.loading && (
                          <div className="text-xs text-zinc-400 font-manrope italic flex items-center gap-2">
                            <Sparkle size={12} weight="fill" className="text-[#FF3333] animate-pulse" />
                            Claude&apos;s thinking through the plan…
                          </div>
                        )}
                        {aiState[c.id]?.err && (
                          <div className="text-xs text-[#FF3333] font-manrope">{aiState[c.id].err}</div>
                        )}
                        {aiState[c.id]?.suggestions && (
                          <div className="border-l-2 border-[#FF3333] bg-[#FF3333]/5 pl-3 py-2 space-y-3" data-testid={`ai-suggest-result-${c.id}`}>
                            <div className="flex items-center gap-2">
                              <Sparkle size={12} weight="fill" className="text-[#FF3333]" />
                              <div className="id-label text-[#FF3333]">Claude suggests</div>
                              {aiState[c.id].cached && <span className="text-[9px] font-manrope uppercase tracking-[0.15em] text-zinc-500">cached</span>}
                            </div>
                            {aiState[c.id].suggestions.workout_swaps?.length > 0 && (
                              <SwapList title="Workout swaps" icon={Barbell} items={aiState[c.id].suggestions.workout_swaps} />
                            )}
                            {aiState[c.id].suggestions.meal_swaps?.length > 0 && (
                              <SwapList title="Meal swaps" icon={ForkKnife} items={aiState[c.id].suggestions.meal_swaps} />
                            )}
                            {aiState[c.id].suggestions.coaching_notes?.length > 0 && (
                              <div>
                                <div className="text-[10px] font-manrope uppercase tracking-[0.15em] text-zinc-500 mb-1.5 flex items-center gap-1">
                                  <Lightbulb size={10} weight="fill" className="text-yellow-500" /> Coaching notes · tap to add to reply
                                </div>
                                <ul className="space-y-1.5">
                                  {aiState[c.id].suggestions.coaching_notes.map((n, idx) => (
                                    <li key={idx}>
                                      <button
                                        onClick={() => appendCoachingNote(c.id, n)}
                                        className="text-xs font-manrope text-zinc-200 hover:text-white text-left w-full border border-[#262626] hover:border-[#FF3333]/50 px-2.5 py-1.5 transition-colors"
                                        data-testid={`ai-note-${c.id}-${idx}`}
                                      >
                                        {n}
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {(aiState[c.id].suggestions.workout_swaps?.length || 0) +
                              (aiState[c.id].suggestions.meal_swaps?.length || 0) +
                              (aiState[c.id].suggestions.coaching_notes?.length || 0) === 0 && (
                              <div className="text-xs text-zinc-500 italic">Nothing to swap this week — client&apos;s dialled in.</div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Reply composer + actions */}
                      <div className="border-t border-[#262626] pt-3 mt-3">
                        <div className="id-label mb-2 text-zinc-500">Your reply — sent as a DM + push</div>
                        <textarea
                          value={replies[c.id] || ""}
                          onChange={(e) => setReplies((r) => ({ ...r, [c.id]: e.target.value }))}
                          rows={3}
                          placeholder="Adjustments, kudos, quick coaching note…"
                          className="w-full bg-[#0F0F0F] border border-[#262626] text-white text-sm font-manrope px-3 py-2 focus:border-[#FF3333] outline-none resize-none"
                          data-testid={`sunday-reply-${c.id}`}
                        />
                        <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
                          <div className="text-[10px] font-manrope uppercase tracking-[0.15em] text-zinc-500">
                            Submitted {new Date(c.submitted_at).toLocaleString()}
                            {c.coach_reviewed_at && <span className="text-[#00CC66]"> · Reviewed {new Date(c.coach_reviewed_at).toLocaleString()}</span>}
                          </div>
                          <div className="flex gap-2">
                            {row.status === "pending" && (
                              <button
                                onClick={() => markReviewed(row)}
                                disabled={sending[c.id]}
                                className="text-[10px] font-manrope uppercase tracking-[0.15em] border border-[#262626] text-zinc-400 hover:text-white hover:border-white px-2.5 py-1.5 disabled:opacity-40"
                                data-testid={`sunday-mark-reviewed-${c.id}`}
                              >
                                Mark reviewed (no reply)
                              </button>
                            )}
                            <button
                              onClick={() => sendReply(row)}
                              disabled={sending[c.id] || !replies[c.id]?.trim()}
                              className="inline-flex items-center gap-1.5 text-[10px] font-oswald uppercase tracking-[0.15em] font-bold bg-[#FF3333] hover:bg-[#E62E2E] text-white px-3 py-1.5 disabled:opacity-40"
                              data-testid={`sunday-reply-send-${c.id}`}
                            >
                              <PaperPlaneTilt size={11} weight="fill" /> {sending[c.id] ? "Sending…" : "Send reply"}
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <Link
                          to={`/coach/clients/${row.client_id}`}
                          className="inline-flex items-center gap-1 text-[10px] font-manrope uppercase tracking-[0.15em] text-zinc-500 hover:text-white"
                          data-testid={`sunday-open-profile-${row.client_id}`}
                        >
                          Open profile · adjust plan <CaretRight size={11} weight="bold" />
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function StatTile({ label, value, color, border }) {
  return (
    <div className={"border px-3 py-3 " + border}>
      <div className="id-label text-zinc-500 mb-1">{label}</div>
      <div className={"font-oswald text-2xl leading-none " + color}>{value}</div>
    </div>
  );
}

function AnswerBlock({ icon: Icon, label, text, compact }) {
  const empty = !text || !text.trim();
  return (
    <div className={"border border-[#262626] bg-[#0A0A0A] px-3 py-2 " + (compact ? "" : "")}>
      <div className="id-label text-zinc-500 mb-1 flex items-center gap-1"><Icon size={10} weight="fill" /> {label}</div>
      {empty ? (
        <div className="text-xs text-zinc-700 italic font-manrope">— skipped —</div>
      ) : (
        <div className="text-sm text-zinc-200 font-manrope whitespace-pre-wrap">{text}</div>
      )}
    </div>
  );
}

function WellnessBlock({ w, clientId }) {
  // Compact wellness snapshot for the coach — pills for each metric with
  // low-value warning colouring on energy/stress so problems jump out.
  const metrics = [
    { k: "sleep", l: "Sleep" },
    { k: "energy", l: "Energy", lowIsBad: true },
    { k: "libido", l: "Libido" },
    { k: "water_intake", l: "Water" },
    { k: "physical_activity", l: "Activity" },
    { k: "body_image", l: "Body img" },
    { k: "stress_levels", l: "Stress", highIsBad: true },
  ];
  const moodMap = {
    happy: { Icon: Smiley, l: "Happy", cls: "text-[#00CC66]" },
    flat:  { Icon: SmileyMeh, l: "Flat", cls: "text-zinc-400" },
    sad:   { Icon: SmileySad, l: "Sad", cls: "text-[#FF3333]" },
  };
  const mood = moodMap[w.mood] || moodMap.flat;
  const MoodIcon = mood.Icon;
  return (
    <div className="border border-[#262626] bg-[#0A0A0A] px-3 py-2" data-testid={`coach-wellness-${clientId}`}>
      <div className="id-label text-zinc-500 mb-2 flex items-center gap-1"><Pulse size={10} weight="fill" /> Wellness (0–10)</div>
      <div className="flex flex-wrap gap-1.5 items-center">
        {metrics.map((m) => {
          const v = w[m.k];
          const bad = (m.lowIsBad && v <= 3) || (m.highIsBad && v >= 7);
          return (
            <div
              key={m.k}
              className={"px-2 py-1 border text-[11px] font-manrope " + (bad ? "border-[#FF3333]/60 bg-[#FF3333]/10 text-[#FF3333]" : "border-[#262626] text-zinc-300")}
              data-testid={`coach-wellness-${clientId}-${m.k}`}
            >
              <span className="text-zinc-500">{m.l}</span> <span className="font-bold">{v}</span>
            </div>
          );
        })}
        <div className={"px-2 py-1 border border-[#262626] text-[11px] font-manrope flex items-center gap-1 " + mood.cls}>
          <MoodIcon size={12} weight="fill" /> {mood.l}
        </div>
      </div>
    </div>
  );
}

function SwapList({ title, icon: Icon, items }) {
  return (
    <div>
      <div className="text-[10px] font-manrope uppercase tracking-[0.15em] text-zinc-500 mb-1.5 flex items-center gap-1">
        <Icon size={10} weight="fill" className="text-[#FF3333]" /> {title}
      </div>
      <ul className="space-y-1.5">
        {items.map((s, i) => (
          <li key={i} className="text-xs font-manrope border border-[#262626] px-2.5 py-1.5">
            <div className="text-white">
              <span className="text-zinc-500">Swap</span> <span className="font-bold">{s.from}</span> <span className="text-zinc-500">→</span> <span className="text-[#00CC66] font-bold">{s.to}</span>
            </div>
            {s.why && <div className="text-zinc-400 italic mt-0.5">{s.why}</div>}
          </li>
        ))}
      </ul>
    </div>
  );
}
